import { useState } from 'react'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { PlansPlanMode } from '../../../api/types'
import { showAsFormattedRupeeAmount } from '../../../../display-values/lumpsumPlanAmount'
import type { LumpsumRebalanceBuyPlanRecommendationDetails } from '../../api/rebalanceRecommendation.types'
import {
  areCounterFundsActionsCompleted,
  PlanRecommendationIntro
} from '../../shared/sheet/PlanRecommendationIntro'
import { PlanRecommendationSheetSkeleton } from '../../shared/sheet/PlanRecommendationSheetSkeleton'
import { PlansScreen } from '../../../../analytics/plansEvents'
import { PlanRecommendationSheetLayout } from '../../shared/sheet/PlanRecommendationSheetLayout'
import { PlanRecommendationStatusReasonBannerSection } from '../../shared/sheet/PlanRecommendationStatusReasonBannerSection'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { RebalanceBuyPlanRecommendationBody } from './body/RebalanceBuyPlanRecommendationBody'
import { RebalanceBuyPlanRecommendationHeader } from './header/RebalanceBuyPlanRecommendationHeader'

type RebalanceBuyPlanRecommendationSheetProps = {
  onDismiss: () => void
  isin: string
  planDetails: LumpsumRebalanceBuyPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}

export function RebalanceBuyPlanRecommendationSheet({
  onDismiss,
  isin,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  onMissedGainsInfoClick,
  transactionCta
}: RebalanceBuyPlanRecommendationSheetProps) {
  const [selectedSellIndex, setSelectedSellIndex] = useState(0)

  // Buy-entry: root totalTransferAmount is buy; other side = sum of rounded sell card amounts.
  const buyAmount = showAsFormattedRupeeAmount({
    rawAmount: planDetails?.totalTransferAmount ?? 0
  }).value
  const sellAmount = (planDetails?.fundsToBeSold ?? []).reduce(
    (sum, fund) =>
      sum + showAsFormattedRupeeAmount({ rawAmount: fund?.sellAmount ?? 0 }).value,
    0
  )

  return (
    <PlanRecommendationSheetLayout
      onDismiss={onDismiss}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      planMode={PlansPlanMode.REBALANCE}
      analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
      fundName={planDetails?.fundName ?? ''}
      fundIsin={isin}
    >
      {!isLoading ? (
        <PlanRecommendationStatusReasonBannerSection planDetails={planDetails} analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation} />
      ) : null}
      {isLoading ? (
        <PlanRecommendationSheetSkeleton planMode={PlansPlanMode.REBALANCE} detailsTitle="Buy details" />
      ) : (
        <>
          <PlanRecommendationIntro
            planMode={PlansPlanMode.REBALANCE}
            buyAmount={buyAmount}
            sellAmount={sellAmount}
            areBuyActionsCompleted={planDetails?.executed === true}
            areSellActionsCompleted={areCounterFundsActionsCompleted(planDetails?.fundsToBeSold)}
            entryAction={LumpsumRecommendationActionType.BUY}
            counterFundCount={planDetails?.fundsToBeSold?.length ?? 0}
          />
          <RebalanceBuyPlanRecommendationHeader
            planDetails={planDetails}
            entryIsin={isin}
            selectedSellIndex={selectedSellIndex}
            onSelectSellIndex={setSelectedSellIndex}
            transactionCta={transactionCta}
          />
          <RebalanceBuyPlanRecommendationBody
            planDetails={planDetails}
            selectedSellIndex={selectedSellIndex}
            onSelectSellIndex={setSelectedSellIndex}
            onMissedGainsInfoClick={onMissedGainsInfoClick}
          />
        </>
      )}
    </PlanRecommendationSheetLayout>
  )
}
